import {
  tocDesktopClass,
  tocDesktopColumnClass,
  tocDesktopDetailsClass,
  tocDesktopLinkClass,
  tocDesktopLinkIndent1Class,
  tocDesktopLinkIndent2Class,
  tocDesktopNavClass,
  tocDesktopScrollClass,
  tocDesktopSummaryClass,
  tocDetailsClass,
  tocInlineClass,
  tocInlineLinkClass,
  tocInlineLinkIndent1Class,
  tocInlineLinkIndent2Class,
  tocInlineNavClass,
  tocInlineScrollClass,
  tocScrollGradientBottomClass,
  tocScrollGradientTopClass,
  tocScrollWrapperClass,
  tocSummaryClass,
  tocSummaryIconClass,
} from "@repo/fika-blog/src/table-of-contents.css"
import { createOverlayScrollbars } from "overlayscrollbars-solid"
import { createSignal, For, onCleanup, onMount } from "solid-js"
import type { TocHeading } from "@/lib/heading-processor"
import { Icon } from "./icon"

interface Props {
  headings: TocHeading[]
  label: string
  variant: "desktop" | "inline"
}

function getIndentClass(
  level: number,
  indent1: string,
  indent2: string,
): string {
  if (level === 3) return indent1
  if (level >= 4) return indent2
  return ""
}

function handleTocClick(e: MouseEvent) {
  const link = e.currentTarget as HTMLAnchorElement
  const href = link.getAttribute("href")
  if (!href) return
  const target = document.querySelector(href)
  if (!target) return
  e.preventDefault()
  target.scrollIntoView({ behavior: "smooth", block: "start" })
  history.pushState(null, "", href)
}

export function TableOfContents(props: Props) {
  let navRef: HTMLDivElement | undefined
  const [showTopGradient, setShowTopGradient] = createSignal(false)
  const [showBottomGradient, setShowBottomGradient] = createSignal(false)

  const [initOS, getInstance] = createOverlayScrollbars({
    options: { scrollbars: { autoHide: "move", autoHideDelay: 800 } },
  })

  function updateGradients() {
    const osInstance = getInstance()
    if (!osInstance) return
    const viewport = osInstance.elements().viewport
    const { scrollTop, scrollHeight, clientHeight } = viewport
    setShowTopGradient(scrollTop > 0)
    setShowBottomGradient(scrollTop + clientHeight < scrollHeight - 1)
  }

  onMount(() => {
    if (!navRef) return

    initOS({ target: navRef })

    requestAnimationFrame(() => {
      const osInstance = getInstance()
      if (!osInstance) return

      const viewport = osInstance.elements().viewport
      viewport.addEventListener("scroll", updateGradients, { passive: true })
      updateGradients()

      const details = navRef?.closest("details")
      const onToggle = () =>
        requestAnimationFrame(() => {
          osInstance.update()
          updateGradients()
        })
      details?.addEventListener("toggle", onToggle)

      const ro = new ResizeObserver(() => {
        osInstance.update()
        updateGradients()
      })
      ro.observe(navRef!)

      onCleanup(() => {
        viewport.removeEventListener("scroll", updateGradients)
        details?.removeEventListener("toggle", onToggle)
        ro.disconnect()
      })
    })
  })

  return (
    <>
      {props.variant === "desktop" ? (
        <div class={tocDesktopColumnClass}>
          <aside class={tocDesktopClass}>
            <details class={tocDesktopDetailsClass} open>
              <summary class={tocDesktopSummaryClass}>
                <span>{props.label}</span>
                <Icon
                  name="chevron-down"
                  size="12"
                  color="sand500"
                  class={tocSummaryIconClass}
                />
              </summary>
              <div class={tocScrollWrapperClass}>
                <div
                  class={tocScrollGradientTopClass}
                  data-visible={showTopGradient() ? "" : undefined}
                />
                <nav class={tocDesktopNavClass} aria-label={props.label}>
                  <div
                    ref={el => {
                      navRef = el
                    }}
                    class={tocDesktopScrollClass}
                  >
                    <For each={props.headings}>
                      {heading => (
                        <a
                          href={`#${heading.id}`}
                          class={[
                            tocDesktopLinkClass,
                            getIndentClass(
                              heading.level,
                              tocDesktopLinkIndent1Class,
                              tocDesktopLinkIndent2Class,
                            ),
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          data-toc-link
                          onClick={handleTocClick}
                        >
                          {heading.text}
                        </a>
                      )}
                    </For>
                  </div>
                </nav>
                <div
                  class={tocScrollGradientBottomClass}
                  data-visible={showBottomGradient() ? "" : undefined}
                />
              </div>
            </details>
          </aside>
        </div>
      ) : (
        <aside class={tocInlineClass}>
          <details class={tocDetailsClass}>
            <summary class={tocSummaryClass}>
              <span>{props.label}</span>
              <Icon
                name="chevron-down"
                size="18"
                color="sand500"
                class={tocSummaryIconClass}
              />
            </summary>
            <div class={tocScrollWrapperClass}>
              <div
                class={tocScrollGradientTopClass}
                data-visible={showTopGradient() ? "" : undefined}
              />
              <nav class={tocInlineNavClass} aria-label={props.label}>
                <div
                  ref={el => {
                    navRef = el
                  }}
                  class={tocInlineScrollClass}
                >
                  <For each={props.headings}>
                    {heading => (
                      <a
                        href={`#${heading.id}`}
                        class={[
                          tocInlineLinkClass,
                          getIndentClass(
                            heading.level,
                            tocInlineLinkIndent1Class,
                            tocInlineLinkIndent2Class,
                          ),
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        data-toc-link
                        onClick={handleTocClick}
                      >
                        {heading.text}
                      </a>
                    )}
                  </For>
                </div>
              </nav>
              <div
                class={tocScrollGradientBottomClass}
                data-visible={showBottomGradient() ? "" : undefined}
              />
            </div>
          </details>
        </aside>
      )}
    </>
  )
}
